/**
 * What a failed API call means for the session, without React.
 *
 * Two decisions live here so they can be tested on their own:
 *
 *   - **`/auth/me` failed — sign in again, or wait?** Only a 401/403 says the
 *     session is gone. A dropped connection, a 5xx or `SERVICE_STARTING` while
 *     the backend restarts says nothing about the session, and bouncing
 *     everyone to /login on a server hiccup is how a thirty-second restart
 *     becomes a sign-in storm. Those are retried briefly, then shown as an
 *     error with a way to try again.
 *   - **Some other call answered 401 — has the session expired mid-use?** Then
 *     `me` is re-checked, which lets `ProtectedRoute` send the person to sign
 *     in with a return path. The `me` query itself is excluded, or its own 401
 *     would re-trigger itself.
 *
 * Deliberately import-free and erasable-syntax TypeScript, so
 * `tests/session-error.test.mjs` can import it with Node's type stripping.
 */

/** Retries of `/auth/me` after the first failure, for transient errors only. */
export const ME_MAX_RETRIES = 2;

/** The HTTP status carried by an `ApiError` (`0` = no response), or null. */
export function errorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

/** True when `/auth/me` failed because there is no valid session. */
export function isSessionRejected(error: unknown): boolean {
  const status = errorStatus(error);
  if (status === 403 && bodyCode(error) === "DEMO_PERSONA_INVALID") {
    // 19 Oct: only the tab's demo persona ended; the session is fine. The API
    // client forgets the persona and reads `me` again as the admin.
    return false;
  }
  return status === 401 || status === 403;
}

function bodyCode(error: unknown): unknown {
  if (typeof error !== "object" || error === null) {
    return null;
  }
  const body = (error as { body?: unknown }).body;
  return typeof body === "object" && body !== null ? (body as { code?: unknown }).code : null;
}

/**
 * True when a failed query is worth running again: a 5xx, say, but not a
 * 401/403, and not a request with no answer (status 0) — the API client has
 * already retried that one, backing off over about 12 s (12 Oct), so a retry
 * here would only multiply the wait and the logged rows.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
  maxRetries: number,
): boolean {
  return !isSessionRejected(error) && errorStatus(error) !== 0 && failureCount < maxRetries;
}

/** `retry` for the `me` query: never on 401/403 or no answer, briefly on anything else. */
export function shouldRetryMe(failureCount: number, error: unknown): boolean {
  return shouldRetryQuery(failureCount, error, ME_MAX_RETRIES);
}

/** `retryDelay` for the `me` query: 0.5 s, then 1 s — short enough to feel like loading. */
export function meRetryDelay(attemptIndex: number): number {
  return Math.min(500 * 2 ** attemptIndex, 2000);
}

/**
 * True when a failure elsewhere should make the app re-check the session.
 *
 * Only a 401 — a 403 on an ordinary call is "not yours" (someone else's
 * ticket, an admin page), not "signed out".
 */
export function shouldRecheckSession(
  error: unknown,
  queryKey: readonly unknown[] | undefined,
): boolean {
  if (errorStatus(error) !== 401) {
    return false;
  }
  return queryKey === undefined || queryKey[0] !== "me";
}
